/**
 * CAOS-573 — the component reference as text, moved here so it has exactly one home.
 *
 * This used to live inside the host's `component-reference.ts`, reachable only by the in-product
 * Setup page. It is the SAME declarations the DOM page reads — `CatalogEntry` and `ComponentDocs`
 * — walked into a different shape, which is the whole point: the in-product page and anything else
 * that wants a component's reference (a public site's build step, an assistant, a CLI) call this
 * one function rather than each keeping its own idea of what a piece's page contains. Two renderers
 * reading two copies of the same knowledge is exactly the arrangement that lets them drift; one
 * function two callers cannot.
 *
 * Nothing in this file knows the name of any component of the library, for the same reason nothing
 * in the DOM reference page does (see `component-docs.ts`): a piece that arrives from a package
 * this file has never seen renders exactly as completely as one compiled in, because both are read
 * the same way — off what the piece declared, never off a name checked against a list.
 */
import type { CatalogEntry } from './catalog.js';
import { PLACEMENT_LABELS, exampleNodes } from './component-docs.js';
import type { ComponentDocs, ComponentInputDoc } from './component-docs.js';
import { markupParts, placementGap, placementSectionJson } from './placement.js';

/** "a, b and c" — so a list of places reads as a sentence rather than as data. */
export function readOut(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The declared placements, read out in words. The line that stops a frame part on a record page. */
export function placementSentence(docs: ComponentDocs | undefined, entry: CatalogEntry): string {
  if (entry.tags.length === 0) {
    return 'Not placed on a page at all — this piece is used by other pieces and is offered nowhere.';
  }
  const places = docs?.placements;
  if (places === undefined) return 'Not declared yet — this piece has not said where it may be placed.';
  if (places.length === 0) return 'Nowhere yet — this piece is not offered to a page.';
  return `May be placed on ${readOut(places.map((p) => PLACEMENT_LABELS[p]))}.`;
}

/** A required flag reads as a word, not a tick — it is the column people scan hardest. */
export const requiredCell = (required: boolean | undefined): string => (required === true ? 'Required' : 'Optional');

/** A default that was never declared and a default of `null` are different, so they read differently. */
export const defaultCell = (value: unknown): string => {
  if (value === undefined) return 'none declared';
  if (value === null) return 'null';
  if (value === '') return 'empty';
  // CAOS-653 — a list default. The empty one is the case worth the branch: "defaults to nothing in
  // it" and "has no default at all" are different facts about a piece, and `String([])` is the
  // empty string, which would draw the first of them as a blank cell and lose it entirely.
  if (Array.isArray(value)) return value.length === 0 ? 'empty list' : `[${value.map((v) => String(v)).join(', ')}]`;
  return String(value);
};

/**
 * How an input is SET — a different question from what it is called, and the one an author is
 * actually asking when they look at this column.
 *
 * Three states, because the library really has three. An input with no attribute can only be
 * assigned. An input with an attribute AND a property accessor can be written either way. An input
 * with an attribute and no accessor is markup only: `base-element.ts` defines no
 * `attributeChangedCallback` and no `observedAttributes`, so attributes are not reflected into
 * properties and assigning one sets a plain expando the piece never reads. That last case is the
 * majority — the accessor is the exception, which is why `property` marks the exceptions rather
 * than every input carrying a flag saying it is ordinary (CAOS-653).
 */
export const attributeCell = (input: ComponentInputDoc): string => {
  if (input.attribute === undefined) return 'property only';
  return input.property === true ? `\`${input.attribute}\`, also a property` : `\`${input.attribute}\`, markup only`;
};

/**
 * The type, and — where the piece named them — the values it actually accepts.
 *
 * A bare `text` for an input that only ever takes one of four words is true and useless. Where a
 * closed set was declared it is the answer. Where a `fallback` was declared too, the set is OPEN:
 * the named values are the ones the piece RECOGNISES, and everything else is handled rather than
 * refused, so saying "one of" would assert a constraint the piece does not enforce (CAOS-653).
 */
export const typeCell = (input: ComponentInputDoc): string => {
  const named = input.options?.length ? input.options.join(', ') : undefined;
  if (input.fallback !== undefined) {
    return named === undefined
      ? `${input.type} — anything it does not recognise reads as \`${input.fallback}\``
      : `${input.type} — recognises ${named}; anything else reads as \`${input.fallback}\``;
  }
  return named === undefined ? input.type : `${input.type} — one of ${named}`;
};

/**
 * What the input is for, plus what reading it gives when that is not what writing it sets
 * (CAOS-653). The asymmetry is otherwise invisible: the piece is documented as taking one value,
 * and nothing says the getter and the setter are talking about two different things.
 */
export const descriptionCell = (input: ComponentInputDoc): string =>
  input.readsBack === undefined ? input.description : `${input.description} Reading it back gives ${input.readsBack}`;

/**
 * Everything a component's reference page shows, as markdown, for something that is about to write
 * the code — or, since CAOS-573, for a static build step that has no DOM to draw a page into at all.
 *
 * Generated from the same declarations the interactive page is drawn from — one source, two
 * renderings — so a reader and an assistant, or the in-product page and a public site, cannot be
 * given two different accounts of the same piece.
 */
export function referenceMarkdown(entry: CatalogEntry, docs: ComponentDocs | undefined): string {
  const out: string[] = [];
  out.push(`# ${entry.name}`);
  if (entry.tags.length > 0) out.push(`\`${entry.tags.map((t) => `<${t}>`).join('`, `')}\``);
  out.push('', entry.purpose, '', placementSentence(docs, entry), '');

  const section = (title: string, lines: readonly string[] | undefined, none: string): void => {
    out.push(`## ${title}`, '');
    if (lines === undefined) out.push('Not documented yet — this piece has not said.', '');
    else if (lines.length === 0) out.push(none, '');
    else out.push(...lines, '');
  };

  section(
    'Inputs',
    docs?.inputs?.length
      ? [
          '| Name | Attribute | Description | Type | Default | Required |',
          '| --- | --- | --- | --- | --- | --- |',
          ...docs.inputs.map(
            (i) =>
              `| \`${i.name}\` | ${attributeCell(i)} | ${descriptionCell(i)} | ${typeCell(i)} | ${defaultCell(i.default)} | ${requiredCell(i.required)} |`,
          ),
        ]
      : docs?.inputs === undefined
        ? undefined
        : [],
    'A page sets nothing on this piece.',
  );

  section(
    'Events',
    docs?.events?.length
      ? ['| Name | When it fires | What it carries |', '| --- | --- | --- |', ...docs.events.map((e) => `| \`${e.name}\` | ${e.when} | ${e.carries} |`)]
      : docs?.events === undefined
        ? undefined
        : [],
    'This piece raises no events.',
  );

  section(
    'Slots',
    docs?.slots?.length
      ? ['| Name | What goes in it |', '| --- | --- |', ...docs.slots.map((s) => `| ${s.name === '' ? 'the default slot' : `\`${s.name}\``} | ${s.takes} |`)]
      : docs?.slots === undefined
        ? undefined
        : [],
    'Nothing goes inside this piece.',
  );

  section(
    'Styling hooks',
    docs?.tokens?.length
      ? ['| Design token | What it controls |', '| --- | --- |', ...docs.tokens.map((t) => `| \`${t.token}\` | ${t.controls} |`)]
      : docs?.tokens === undefined
        ? undefined
        : [],
    'This piece honours no design tokens of its own.',
  );

  section(
    'Methods',
    docs?.methods?.length
      ? [
          '| Name | Description | Arguments |',
          '| --- | --- | --- |',
          ...docs.methods.map(
            (m) =>
              `| \`${m.name}()\` | ${m.description} | ${m.args === undefined ? 'not stated' : m.args.length === 0 ? 'takes none' : m.args.map((a) => `${a.name}: ${a.type}`).join(', ')} |`,
          ),
        ]
      : docs?.methods === undefined
        ? undefined
        : [],
    'Nothing else drives this piece by calling it.',
  );

  section(
    'Readable state',
    docs?.state?.length
      ? ['| Name | Type | What it tells you |', '| --- | --- | --- |', ...docs.state.map((x) => `| \`${x.name}\` | ${x.type} | ${x.description} |`)]
      : docs?.state === undefined
        ? undefined
        : [],
    'Nothing on this piece can be read back.',
  );

  section(
    'Styling parts',
    docs?.parts?.length
      ? [
          // CAOS-653 — the third column. A part was declared as though it is always exposed, so a
          // piece that only exposes one in a particular mode could say so only in prose. "Always"
          // is stated rather than left blank, so the ordinary case is a claim and not an omission.
          '| Part | Which piece of it | When it is there |',
          '| --- | --- | --- |',
          ...docs.parts.map((x) => `| \`::part(${x.part})\` | ${x.is} | ${x.when ?? 'Always'} |`),
        ]
      : docs?.parts === undefined
        ? undefined
        : [],
    'This piece exposes none of its insides to a stylesheet.',
  );

  if (docs?.whenToUse) out.push('## When to use it', '', docs.whenToUse, '');
  if (docs?.insteadUse?.length) {
    out.push('## What to use instead', '', ...docs.insteadUse.map((a) => `- **${a.name}** — ${a.when}`), '');
  }
  if (docs?.accessibility) out.push('## Accessibility', '', docs.accessibility, '');
  // Gated on `!== undefined`, identically to the in-product page's own condition. The premise of
  // having two renderers read one declaration is that they cannot say different things about it,
  // and a truthiness check here against an `=== undefined` check there is exactly how they would:
  // a piece declaring an empty string would get the section on one surface and not the other.
  if (docs?.provenance !== undefined) out.push('## Where the artwork comes from', '', docs.provenance, '');

  if (docs?.examples?.length) {
    out.push('## Examples', '');
    for (const example of docs.examples) {
      const nodes = exampleNodes(example);
      out.push(`### ${example.label}`, '', example.summary, '');
      if (example.host) out.push(`The page around it: ${example.host.summary}`, '');
      const gap = nodes.map(placementGap).find((g): g is string => g !== undefined);
      if (gap === undefined) out.push('```json', placementSectionJson(nodes, example.label), '```', '');
      else out.push(`_Cannot be shown as a page placement: ${gap}_`, '');
      for (const part of markupParts(nodes)) out.push(`\`\`\`${part.language}`, part.text, '```', '');
    }
  }
  return out.join('\n');
}
